import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { X, AlertTriangle } from 'lucide-react';
import { clientsAPI } from '@shared/apiClient';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const reasonLabels = {
  skipped_to_next_cycle: 'Skipped to next cycle',
  pushed_back_next_month: 'Bumped to next month',
  pushed_back_future_month: 'Bumped to future month',
  not_in_current_month: 'Not in current month'
};

export default function MonthPairComparisonModal({ isOpen, onClose }) {
  const { data, isLoading, isError } = useQuery(
    'monthPairDiff',
    () => clientsAPI.getMonthPairDiff(),
    { enabled: isOpen }
  );

  const responseData = data?.data;
  const excludedClients = responseData?.excludedClients || [];
  const totals = responseData?.totals || {};
  const breakdown = responseData?.breakdown || {};
  const overallBreakdown = responseData?.overallBreakdown || {};
  const assignedClients = responseData?.assignedClients || [];
  const monthPair = responseData?.monthPair;
  const [showAssigned, setShowAssigned] = useState(false);

  const pairLabel = useMemo(() => {
    if (!monthPair?.pair || monthPair.pair.length !== 2) {
      return 'Current Month Pair';
    }
    const [first, second] = monthPair.pair;
    return `${monthNames[first - 1]}/${monthNames[second - 1]}`;
  }, [monthPair]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-3xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Month Pair Comparison - {pairLabel}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mb-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
            </div>
            <div className="ml-3 text-sm text-yellow-800">
              This compares all clients in the current month pair to those included in Regular Service.
              Excluded clients show a basic reason based on the current service logic.
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : isError ? (
          <div className="text-sm text-red-600">Failed to load comparison data.</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 border border-gray-200 rounded p-4">
                <div className="text-sm text-gray-500">Month Pair Total</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {totals.pairCount || 0}
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded p-4">
                <div className="text-sm text-gray-500">Regular Service</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {totals.regularCount || 0}
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded p-4">
                <div className="text-sm text-gray-500">Excluded</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {totals.excludedCount || 0}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-base font-semibold text-gray-900 mb-3">
                Month Pair Regular Service Breakdown
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Regular Total</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {breakdown.total || 0}
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Assigned</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {breakdown.assigned || 0}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAssigned((prev) => !prev)}
                    className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    {showAssigned ? 'Hide assigned list' : `View assigned (${assignedClients.length})`}
                  </button>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Returned</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {breakdown.returned || 0}
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Completed</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {breakdown.completed || 0}
                  </div>
                  <div className="text-xs text-gray-500">
                    Finalized: {breakdown.finalized || 0} / Not Finalized: {breakdown.completedNotFinalized || 0}
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Unassigned</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {breakdown.unassigned || 0}
                  </div>
                </div>
              </div>

              {showAssigned && (
                <div className="mb-6">
                  <h5 className="text-sm font-semibold text-gray-900 mb-2">
                    Assigned Clients
                  </h5>
                  {assignedClients.length === 0 ? (
                    <div className="text-sm text-gray-500">No assigned clients in this list.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Customer
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Technician
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Assigned Date
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {assignedClients.map((client) => {
                            const displayName = client.isCommercial && client.companyName
                              ? client.companyName
                              : `${client.contactFirstName || ''} ${client.contactLastName || ''}`.trim();
                            const techName = client.technician
                              ? `${client.technician.firstName} ${client.technician.lastName}`
                              : 'Unassigned';
                            const assignedDate = client.assignedAt
                              ? new Date(client.assignedAt).toLocaleDateString()
                              : 'N/A';
                            return (
                              <tr key={client.id}>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {displayName || client.companyName || 'N/A'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700">
                                  {techName}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700">
                                  {assignedDate}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <h4 className="text-base font-semibold text-gray-900 mb-3">
                Overall Regular Service Breakdown (All Contract Clients)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Regular Total</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {overallBreakdown.total || 0}
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Assigned</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {overallBreakdown.assigned || 0}
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Returned</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {overallBreakdown.returned || 0}
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Completed</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {overallBreakdown.completed || 0}
                  </div>
                  <div className="text-xs text-gray-500">
                    Finalized: {overallBreakdown.finalized || 0} / Not Finalized: {overallBreakdown.completedNotFinalized || 0}
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Unassigned</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {overallBreakdown.unassigned || 0}
                  </div>
                </div>
              </div>

              <h4 className="text-base font-semibold text-gray-900 mb-3">
                Excluded Clients
              </h4>
              {excludedClients.length === 0 ? (
                <div className="text-sm text-gray-500">No exclusions for this month pair.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {excludedClients.map((client) => {
                        const displayName = client.isCommercial && client.companyName
                          ? client.companyName
                          : `${client.contactFirstName || ''} ${client.contactLastName || ''}`.trim();
                        return (
                          <tr key={client.id}>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {displayName || client.companyName || 'N/A'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700">
                              {reasonLabels[client.reason] || client.reason || 'Unknown'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
